import {useContext, useEffect, useState} from 'react';
import {CustomDomainScreenContext} from './base';
import {useNavigate, useParams} from 'react-router-dom';
import {CustomDomainState} from '../../api/types/organization/CustomDomain';
import {BoxWithTitle} from './list';
import {CopyIcon} from 'lucide-react';
import {toast} from 'react-toastify';
import {Button} from '../../ui-components/Button/button';

type LoadingState = 'idle' | 'pending' | 'resolved' | 'rejected';
export default function CustomDomainDelete() {
    const {list, api, onDomainDelete} = useContext(CustomDomainScreenContext);
    const {hostname} = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<LoadingState>('idle');

    const domain = list.find((d) => d.hostname === hostname);

    useEffect(() => {
        if (!domain) {
            navigate('/domains');
            return;
        }
        switch (domain.state) {
            case CustomDomainState.CNAME_PENDING:
            case CustomDomainState.CNAME_VERIFIED:
            case CustomDomainState.DOMAIN_PENDING:
            case CustomDomainState.DOMAIN_VERIFIED:
            case CustomDomainState.DOMAIN_READY:
            case CustomDomainState.DOMAIN_ERROR:
                break;
            default:
                navigate('/domains');
                break;
        }
    }, [domain, navigate]);

    return !!domain ? (
        <BoxWithTitle title={`Delete Domain: ${domain.hostname}`}>
            <div className={'flex flex-col p-6 leading-loose'}>
                {/*anything post CNAME_PENDING might have a CNAME*/}
                {domain.state !== CustomDomainState.CNAME_PENDING ? (
                    <div className={'flex flex-col pb-10'}>
                        <span className={'text-lg'}>Remove the DNS CNAME record</span>
                        <span>
                            Before we can delete the domain, you need to remove the DNS CNAME record that points to
                        </span>
                        <div className={'flex flex-row items-center'}>
                            <input
                                className={'rounded-md border border-gray-300 p-1'}
                                value={domain.hostname}
                                readOnly
                            />
                            <CopyIcon
                                onClick={() => {
                                    navigator.clipboard
                                        .writeText(domain.hostname)
                                        .then(() => {
                                            toast('Copied', {type: 'success'});
                                        })
                                        .catch(() => {
                                            toast('Failed to copy. Please copy the value manually.', {
                                                type: 'error',
                                            });
                                        });
                                }}
                                className={'ml-2 cursor-pointer'}
                            />
                        </div>
                    </div>
                ) : null}
                <div className={'flex flex-col'}>
                    <Button
                        variant={'outline'}
                        size={'sm'}
                        data-disabled={loading === 'pending'}
                        disabled={loading === 'pending'}
                        className={'text-red-500 data-[disabled="true"]:bg-gray-300'}
                        onClick={() => {
                            const confirm = window.confirm('Are you sure you want to delete this domain?');
                            if (!confirm) {
                                return;
                            }
                            setLoading('pending');
                            api
                                ?.delete(domain.hostname)
                                .then(() => {
                                    setLoading('resolved');
                                    toast('Domain deleted.', {type: 'success'});
                                    onDomainDelete(domain);
                                    navigate('/domains', {replace: true});
                                })
                                .catch((err) => {
                                    setLoading('rejected');
                                    toast(`Failed to delete domain: ${err.message}`, {type: 'error'});
                                });
                        }}>
                        {loading === 'pending' ? 'Deleting...' : 'Delete'}
                    </Button>
                </div>
            </div>
        </BoxWithTitle>
    ) : null;
}
